import { ICalls } from '@dataTypes/calls.type'
import { createTwilioToken, saveCall, setCallerInfo } from '@store/actions'
import { AppDispatch } from '@store/index'
import { Device, Call } from '@twilio/voice-sdk'
import { IContact } from '@dataTypes/contact.type'
import { IClinic } from '@dataTypes/clinic.type'
import { v4 as uuidv4 } from 'uuid'
import { dialExtension, dialNumber } from '@services/api/calls.api'
import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'
import { getActiveMainClinicNumber } from '@utils/clinic.util'
import {
  setCurrentCall,
  setDevice,
  setHasActiveCall,
} from '@store/reducers/calls.reducer'
import { setActiveSidePanel } from '@store/reducers/uiSlice.reducer'

export const destroyTwilioVoiceDevice = (
  device: Device | null | undefined,
): void => {
  if (!device) return
  try {
    const calls = device.calls
    if (calls) {
      calls.forEach((c) => {
        try {
          c.disconnect()
        } catch {
          logger.info('Call is already disconnected')
        }
      })
    }
    device.destroy()
  } catch {
    logger.error('Error destroying Twilio voice device')
  }
}

const getOutgoingCallerId = (clinic?: IClinic): string => {
  if (clinic) {
    const n = getActiveMainClinicNumber(clinic, 'call')
    if (n) return n
  }
  return getEnv('REACT_APP_TWILIO_NUMBER').trim()
}

const HandleIncomingCall = (call: Call, dispatch: AppDispatch): void => {
  const recentCallId = localStorage.getItem('recent_call_id')
  const callSid = call.parameters.CallSid || 'Unknown'
  if (recentCallId !== callSid) {
    const from = call.parameters.From || 'Unknown'
    const callerInfo: Partial<ICalls> = {
      receiver_number: getEnv('REACT_APP_TWILIO_NUMBER'),
      caller_number: from,
      call_id: callSid,
      call_type: 'Incoming',
      duration: 0,
      start_date_time: new Date().toISOString(),
      end_date_time: new Date(),
      call_status: 'Ringing',
    }

    localStorage.setItem('recent_call_id', callSid)
    dispatch(setCallerInfo(callerInfo))
    dispatch(setCurrentCall(call))
    dispatch(setActiveSidePanel('phone'))

    const clearIncomingRing = (): void => {
      localStorage.removeItem('recent_call_id')
      dispatch(setCurrentCall(null))
      dispatch(setHasActiveCall(false))
      void dispatch(setCallerInfo(null))
    }

    call.on('cancel', () => {
      logger.info('Twilio incoming call cancelled (remote ended before answer)')
      try {
        call.disconnect()
      } catch {
        /* already ended */
      }
      clearIncomingRing()
    })

    call.on('disconnect', () => {
      dispatch(setCurrentCall(null))
    })
  }
}

const refreshTwilioToken = (device: Device, dispatch: AppDispatch): void => {
  dispatch(createTwilioToken()).then(() => {
    const token = localStorage.getItem('twilio_token')
    if (token) {
      device.updateToken(token)
    }
  })
}

const addDeviceListeners = (device: Device, dispatch: AppDispatch): void => {
  device.on('registered', function () {
    logger.info('Twilio.Device Ready to make and receive calls!')
  })

  device.on('error', function (error) {
    logger.info('Twilio.Device Error', { message: error.message })
    if ('code' in error) {
      logger.info('Twilio.Device Code', { code: error.code })
      switch (error.code) {
        case 31205:
          refreshTwilioToken(device, dispatch)
          break
        default:
          break
      }
    }
  })

  device.on('incoming', (call: Call) => HandleIncomingCall(call, dispatch))
}

const initializeDevice = (
  accessToken: string,
  dispatch: AppDispatch,
): Device => {
  logger.info('Initializing device.')
  const device = new Device(accessToken, {
    logLevel: 3, // 0=trace, 1=debug, 2=info, 3=warn, 4=error, 5=silent
    codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
    allowIncomingWhileBusy: true,
  })

  addDeviceListeners(device, dispatch)
  device.register()

  return device
}

export const createNewTwilioDevice = (
  dispatch: AppDispatch,
  existingDevice?: Device | null,
): void => {
  if (existingDevice) {
    destroyTwilioVoiceDevice(existingDevice)
  }

  dispatch(createTwilioToken()).then(async () => {
    const token = localStorage.getItem('twilio_token')
    if (token) {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true })
        dispatch(setDevice(initializeDevice(token, dispatch)))
      } catch (error) {
        logger.error('Error accessing media devices', error)
      }
    }
  })
}

export const makeOutgoingCall = async (
  device: Device,
  contact: Partial<IContact>,
  dispatch: AppDispatch,
  clinic?: IClinic,
): Promise<void> => {
  logger.info('Initiating outgoing call')
  const localNumber = getEnv('REACT_APP_LOCAL_NUMBER')
  const number = contact.cellphone ?? localNumber
  const params = {
    conferenceName: `ENAMELAI-CONFERENCE-${uuidv4()}`,
    To: number,
  }

  const activeCalls = Array.from(device.calls?.values() || [])
  if (activeCalls.length > 0) {
    logger.info('A connection is already active. Ending the previous call...')
    return
  }

  logger.info('Connecting to phone number')
  const call = await device.connect({ params })

  const fromNumber = getOutgoingCallerId(clinic)

  // Dial the actual number using Twilio API
  if (clinic?.twilio_account_sid && clinic?.twilio_auth_token) {
    await dialNumber(
      clinic.twilio_account_sid,
      clinic.twilio_auth_token,
      params.conferenceName,
      number,
      fromNumber,
    )
  } else {
    logger.error('Missing Twilio credentials for clinic')
  }

  const _callerInfo: Partial<ICalls> = {
    receiver_number: contact.cellphone ?? localNumber,
    caller_number: fromNumber,
    call_id: call.parameters.CallSid,
    call_sid: call.parameters.CallSid,
    call_type: 'Outgoing',
    duration: 0,
    start_date_time: new Date().toISOString(),
    end_date_time: new Date(),
    call_status: 'Ringing',
  }

  call.on('error', () => {
    const failedCallerInfo: Partial<ICalls> = {
      ..._callerInfo,
      call_id: call.parameters.CallSid,
      call_status: 'Failed',
    }
    dispatch(setCallerInfo(failedCallerInfo))
    dispatch(saveCall(failedCallerInfo))
  })

  dispatch(setCurrentCall(call))
  dispatch(setCallerInfo(_callerInfo))
  dispatch(setHasActiveCall(true))

  call.on('disconnect', () => {
    dispatch(setCurrentCall(null))
  })
}

export const makeOutgoingExtensionCall = async (
  device: Device,
  dispatch: AppDispatch,
  clinic: IClinic,
  displayReceiver: string,
  extensionNumberForApi: string,
): Promise<void> => {
  logger.info('Making outgoing extension call')
  const params = {
    conferenceName: `ENAMELAI-CONFERENCE-${uuidv4()}`,
    To: extensionNumberForApi,
  }

  const activeCalls = Array.from(device.calls?.values() || [])
  if (activeCalls.length > 0) {
    logger.info('A connection is already active. Ending the previous call...')
    return
  }

  logger.info('Connecting extension dial (multi-device ring)')
  const call = await device.connect({ params })

  const dbName = localStorage.getItem('db_name') || ''
  const fromNum = getOutgoingCallerId(clinic)

  if (clinic.twilio_account_sid && clinic.twilio_auth_token && dbName) {
    await dialExtension(
      clinic.twilio_account_sid,
      clinic.twilio_auth_token,
      params.conferenceName,
      fromNum,
      clinic.clinic_id,
      extensionNumberForApi.trim(),
      dbName,
    )
  } else {
    logger.error('Missing Twilio credentials or db_name for extension dial')
  }

  const _callerInfo: Partial<ICalls> = {
    receiver_number: displayReceiver,
    caller_number: fromNum,
    call_id: call.parameters.CallSid,
    call_sid: call.parameters.CallSid,
    call_type: 'Outgoing',
    duration: 0,
    start_date_time: new Date().toISOString(),
    end_date_time: new Date(),
    call_status: 'Ringing',
  }

  call.on('error', () => {
    const failedCallerInfo: Partial<ICalls> = {
      ..._callerInfo,
      call_id: call.parameters.CallSid,
      call_status: 'Failed',
    }
    dispatch(setCallerInfo(failedCallerInfo))
    dispatch(saveCall(failedCallerInfo))
  })

  dispatch(setCurrentCall(call))
  dispatch(setCallerInfo(_callerInfo))
  dispatch(setHasActiveCall(true))

  call.on('disconnect', () => {
    dispatch(setCurrentCall(null))
  })
}
